/**
 * Backend API client.
 *
 * Every application read and write goes through here (Decision #38). The
 * browser's Supabase client is for authentication only.
 */
import { supabase } from './supabase.js';

export const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(/\/+$/, '');

/** An error carrying the backend's stable machine-readable code. */
export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Call the Attest API as the signed-in user.
 *
 * The token is pulled fresh from getSession() on every call rather than being
 * captured once: the Supabase SDK rotates it roughly hourly, and a stale
 * captured token would start returning 401s an hour into a working session.
 */
export async function apiFetch(path, options = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;

  const headers = new Headers(options.headers ?? {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let res;
  try {
    res = await fetch(`${baseUrl}${path}`, { ...options, headers });
  } catch (cause) {
    // Network-level failure: server down, DNS, or — most likely during setup —
    // a CORS preflight rejection, which the browser reports as a plain
    // TypeError with no detail. Name that possibility; it is otherwise a
    // genuinely baffling ten minutes.
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      `Could not reach the Attest server at ${baseUrl}. It may be offline, or this ` +
        "site's address may not be on the server's allowed-origins list."
    );
  }

  if (res.status === 401) {
    // The session is genuinely gone (expired, revoked, or the user was
    // deleted). Clear it so the app does not sit in a signed-in state that
    // fails every request.
    await supabase.auth.signOut();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Your session has ended. Please sign in again.');
  }

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    // 403 is NOT a sign-out: the person is legitimately signed in and simply
    // may not do this thing. Signing them out here would be baffling.
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'REQUEST_FAILED',
      body?.error?.message ?? `Request failed (${res.status}).`
    );
  }

  return body?.data ?? body;
}

export default apiFetch;
